import React, { useEffect, useRef, useState } from "react";
import { Stethoscope, Zap, LockOpen, MessageCircle } from "lucide-react";

const CARDS = [
  {
    Icon: Stethoscope,
    title: "Hecho para clínicas mexicanas",
    text: "No es un chatbot genérico. Sofi fue diseñada para el flujo real de clínicas: especialidades, doctores, precios y recordatorios — en español mexicano.",
  },
  // WEB.CL2 (11 Oct 2026): sin plazos de entrega (reglas de copy de Surit).
  {
    Icon: Zap,
    title: "Conexión acompañada",
    text: "En los planes con Sofi, la conexión del WhatsApp de tu clínica la hacemos contigo, paso a paso. El resto se configura desde el panel.",
  },
  {
    Icon: LockOpen,
    title: "Sin contratos ni permanencia",
    // TEN.4: se retiro "si no ves resultados en el primer mes, cancelas sin costo ni tramite":
    // contradice los Terminos de Uso, donde la cancelacion surte efecto al fin del periodo
    // (auditoria #21).
    text: "Suscripción mensual. Sin letra chica.",
  },
  {
    Icon: MessageCircle,
    // WEB.CL2: sin prometer disponibilidad. Habla del CORREO de contacto, no del WhatsApp: el número
    // comercial lo contesta el asistente (DT.269). La dirección no se escribe aquí: llega de
    // /api/publico/alta (contacto_email) y, si no llega, no se pinta (regla de TEN.4).
    title: "Soporte de una persona",
    text: null,
  },
];

function textoSoporte(contactoEmail) {
  return contactoEmail
    ? `Si escribes a ${contactoEmail}, te responde una persona.`
    : "Si escribes al correo de soporte, te responde una persona.";
}

export default function Differentiators({ contactoEmail }) {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="py-24 sm:py-28 bg-white" ref={sectionRef} id="diferenciadores">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-12">
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              color: "#2563EB",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            POR QUÉ ELEGIRNOS
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            No somos otro software de agenda
          </h2>
          <p className="text-slate-600 mt-4 text-lg leading-relaxed">
            Hay decenas de herramientas para clínicas. Esto es lo que nos hace diferentes.
          </p>
        </div>

        {/* 2×2 grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {CARDS.map((card, i) => (
          /* Wrapper handles entrance animation; inner div handles hover */
          <div
            key={card.title}
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(20px)",
              transition: `opacity 0.45s ease ${i * 0.1}s, transform 0.45s ease ${i * 0.1}s`,
            }}
          >
            <div
              style={{
                borderRadius: 20,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: "28px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                height: "100%",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#DBE5FF";
                e.currentTarget.style.boxShadow = "0 8px 24px -12px rgba(15,23,42,.16)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#E2E8F0";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {/* Icon circle */}
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 9999,
                  background: "#DBE5FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <card.Icon size={28} color="#2563EB" strokeWidth={1.75} />
              </div>

              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>
                  {card.title}
                </h3>
                <p style={{ fontSize: 15, color: "#64748B", lineHeight: 1.65, margin: 0 }}>
                  {card.text ?? textoSoporte(contactoEmail)}
                </p>
              </div>
            </div>
          </div>
          ))}
        </div>
      </div>
    </section>
  );
}
